import React, { PropsWithChildren, useState } from 'react';
import { useDropdownMenu, UseDropdownMenuOptions } from 'react-overlays/DropdownMenu';
import { ISheetProps, Sheet } from '../../components/Sheet/Sheet';
import { useDropdown } from './useDropdown';
import { IDropdownBaseProps } from './DropdownContext';
import { MenuBase, MenuSize } from '../Menu/MenuBase';
import styled from 'styled-components';

export interface IResponsiveDropdownMenuProps extends PropsWithChildren<UseDropdownMenuOptions>, IDropdownBaseProps {
  sheetConfig?: ISheetProps;
  itemSize?: MenuSize;
}

type ContentFn = (content: React.ReactNode) => void;

interface IDropdownSheetContext {
  contents?: React.ReactNode[];
  setContent: ContentFn;
  active: number;
  setActive: (active: number) => void;
}

export const DropdownSheetContext = React.createContext<IDropdownSheetContext>({
  contents: [],
  setContent: /* istanbul ignore next */ () => {},
  active: 0,
  setActive: /* istanbul ignore next */ () => {}
});

export const useDropdownSheet = () => React.useContext(DropdownSheetContext);

const MenuSheet = styled(Sheet)``;

const SheetMenu: React.FC<PropsWithChildren<ISheetProps>> = ({ children, ...rest }) => {
  const [contents, setCurrentContents] = useState([children]);
  const [active, setActive] = useState<number>(0);

  const setContent: ContentFn = (c) => {
    setCurrentContents([...contents, c]);
    setActive(active + 1);
  };

  const handleOnBackButtonClick: React.MouseEventHandler = (e) => {
    setActive(active - 1);
    const currentContents = [...contents];
    currentContents.pop();
    setCurrentContents(currentContents);
    e.stopPropagation();
  };

  return (
    <DropdownSheetContext.Provider value={{ contents, setContent, active, setActive }}>
      <MenuSheet peek={false} {...rest} showBackButton={active > 0} onBackButtonClick={handleOnBackButtonClick}>
        {contents[active]}
      </MenuSheet>
    </DropdownSheetContext.Provider>
  );
};

export const DropdownMenu: React.FC<IResponsiveDropdownMenuProps> = ({ children, sheetConfig, flip = true, offset = [0, 8], ...rest }) => {
  const [props, { show, toggle }] = useDropdownMenu({ flip, offset, ...rest });
  const { isMobile } = useDropdown(rest);

  return isMobile ? (
    <SheetMenu {...sheetConfig} show={show} onHide={() => toggle?.(!show)}>
      <MenuBase {...{ ...rest, show }}>{children}</MenuBase>
    </SheetMenu>
  ) : (
    <MenuBase asDropdown={true} {...{ ...rest, show }} {...props}>
      {children}
    </MenuBase>
  );
};
